import type { FeatureType } from "schemas/agent-billing";

import { toast } from "@replohq/lattice/components/toast/Toast";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import {
  agentBillingRouter,
  agentBillingUtils,
} from "@v2/trpc/routes/agentBilling";

import { BILLING_CONFETTI_EVENT } from "../agentBillingToasts";

export function usePurchaseCredits({
  featureType,
}: {
  featureType: FeatureType;
}) {
  const workspaceId = useCurrentWorkspaceId();

  const { mutateAsync, isPending } =
    agentBillingRouter.purchase.purchase.useMutation({
      meta: { reploIgnoreDefaultErrorHandling: true },
    });

  const purchase = async ({ quantity }: { quantity: number }) => {
    if (!workspaceId) {
      return;
    }

    try {
      await mutateAsync({
        workspaceId,
        featureType,
        quantity,
      });

      void agentBillingUtils.credits.find.invalidate();

      toast.success({
        title: "Credits purchased",
        description: `${quantity.toLocaleString()} credits have been added to your account.`,
      });

      window.dispatchEvent(new CustomEvent(BILLING_CONFETTI_EVENT));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to purchase credits";
      toast.error({ title: "Purchase failed", description: errorMessage });
      throw error;
    }
  };

  return {
    purchase,
    isLoading: isPending,
  };
}
